<script lang="ts">
  // Просмотр архива с НАШЕГО хранилища.
  //
  // Раньше архив тянулся с регистратора по RTSP, а тот отдаёт запись строго
  // в реальном времени — поэтому ускоренная перемотка захлёбывалась через
  // полминуты. Теперь запись лежит у нас на диске: отдаём её обычным MP4,
  // и браузер сам умеет и мгновенно прыгать по времени, и играть на любой
  // скорости. Никаких сессий к регистратору и трафика через туннель.
  import { onDestroy } from 'svelte';
  import { clipState, clipUrl } from '../lib/api';

  type Props = {
    streamName: string;
    streamLabel: string;
    fromMs: number;
    toMs: number;
    onclose: () => void;
  };
  let { streamName, streamLabel, fromMs, toMs, onclose }: Props = $props();

  const SPEEDS = [0.5, 1, 2, 4, 8, 16];

  let videoEl: HTMLVideoElement | undefined = $state();
  let ready = $state(false);
  let err = $state('');
  let waited = $state(0);
  let rate = $state(1);
  let cancelled = false;

  const src = clipUrl(streamName, fromMs, toMs, false);

  function fmtClock(sec: number): string {
    const d = new Date(fromMs + sec * 1000);
    return d.toLocaleTimeString('ru-RU');
  }

  let position = $state(0);

  (async () => {
    const started = Date.now();
    try {
      for (;;) {
        if (cancelled) return;
        const st = await clipState(streamName, fromMs, toMs, false);
        if (st === 'ready') break;
        waited = Math.round((Date.now() - started) / 1000);
        await new Promise((r) => setTimeout(r, 1500));
      }
      ready = true;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    }
  })();

  onDestroy(() => {
    cancelled = true;
  });

  function setRate(r: number): void {
    rate = r;
    if (videoEl) videoEl.playbackRate = r;
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') onclose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="bg" onclick={(e) => { if (e.target === e.currentTarget) onclose(); }} role="presentation">
  <div class="modal">
    <div class="head">
      <h2>{streamLabel}</h2>
      <span class="range">
        {new Date(fromMs).toLocaleString('ru-RU')} — {new Date(toMs).toLocaleTimeString('ru-RU')}
      </span>
      <button type="button" class="close" onclick={onclose} title="Закрыть">✕</button>
    </div>

    {#if err}
      <p class="err">{err}</p>
    {:else if !ready}
      <div class="prep">
        <div class="spinner"></div>
        <p>Готовлю запись за этот период…{waited ? ` ${waited} с` : ''}</p>
      </div>
    {:else}
      <!-- svelte-ignore a11y_media_has_caption -->
      <video
        bind:this={videoEl}
        {src}
        controls
        autoplay
        playsinline
        ontimeupdate={() => (position = videoEl?.currentTime ?? 0)}
      ></video>
      <div class="bar">
        <span class="clock">{fmtClock(position)}</span>
        <div class="speeds">
          {#each SPEEDS as sp (sp)}
            <button type="button" class:active={rate === sp} onclick={() => setRate(sp)}>
              {sp === 1 ? '1×' : `${sp}×`}
            </button>
          {/each}
        </div>
        <a class="dl" href={src} download>Скачать</a>
      </div>
    {/if}
  </div>
</div>

<style>
  .bg {
    position: fixed; inset: 0; z-index: 150;
    background: rgba(0,0,0,.6);
    backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
  }
  .modal {
    width: min(1100px, calc(100vw - 32px));
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 16px;
    display: flex; flex-direction: column; gap: 12px;
  }
  .head { display: flex; align-items: center; gap: 12px; }
  h2 { font-size: 15px; font-weight: 600; margin: 0; }
  .range { font-size: 12px; color: var(--fg-muted); flex: 1; }
  .close { color: var(--fg-muted); font-size: 15px; padding: 4px 8px; border-radius: 6px; }
  .close:hover { color: var(--fg); background: rgba(127,127,127,.12); }

  .prep {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 48px 0; color: var(--fg-muted); font-size: 13px;
  }
  .prep p { margin: 0; }
  .spinner {
    width: 24px; height: 24px; border-radius: 50%;
    border: 2px solid var(--border); border-top-color: var(--accent);
    animation: spin .8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  video {
    width: 100%; max-height: calc(100dvh - 220px);
    background: #000; border-radius: 8px; display: block;
  }
  .bar { display: flex; align-items: center; gap: 12px; }
  .clock {
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 13px; color: var(--fg); font-variant-numeric: tabular-nums;
  }
  .speeds { display: flex; gap: 4px; flex: 1; }
  .speeds button {
    font-size: 12px; padding: 4px 9px; border-radius: 6px;
    color: var(--fg-muted); border: 1px solid transparent;
  }
  .speeds button:hover { color: var(--fg); }
  .speeds button.active {
    color: #fff; background: var(--accent); border-color: var(--accent);
  }
  .dl { font-size: 12px; color: var(--accent); text-decoration: none; padding: 4px 8px; }
  .dl:hover { text-decoration: underline; }
  .err { color: var(--bad); font-size: 13px; padding: 24px 0; text-align: center; }
</style>
